import { IAttribute, IModel, IValidationContext, IViolation } from './../base/annotation';

import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { format } from '../../shared/utils/string.util';
import {
    azureCloudTypeDef,
    StorageAccountKind,
} from '../../constants/linkedservice-constants';
import {
    AzureStorageLinkedService,
} from './azure-storage.linkedservice';
import { Metamodel } from '../base/model';
import type { IAzureBlobLinkedServiceValidators } from './azure-blob-fs.linkedservice';
import { AzureKeyVault } from './key-vault';
import { Credential } from '../credential/credential';
import { Constants } from '../../shared/shared-constants';
import { azureDefaultAuthRefTypes, azureStorageAuthTypes } from '../../interfaces/IAuthenticationType';

export const azureBlobStorageLinkedServiceValidators: IAzureBlobLinkedServiceValidators =
    {
        validateAKVLinkedserviceReference: undefined,
    };

@Metamodel.model({
    name: ModelConstants.blobLinkedService,
    display: [
        format(
            shellStringResources.ModelName_LinkedServiceTemplate,
            shellStringResources.AzureBlobStorage_DisplayText
        ),
        format(
            shellStringResources.ModelName_LinkedServicesTemplate,
            shellStringResources.AzureBlobStorage_DisplayText
        ),
    ],
})
export class AzureBlobStorageLinkedService extends AzureStorageLinkedService {
    public static readonly MODEL: IModel;
    public static readonly SERVICE_ENDPOINT: IAttribute;
    public static readonly TENANT: IAttribute;
    public static readonly SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly SERVICE_PRINCIPAL_KEY: IAttribute;
    public static readonly CONTAINER_URI: IAttribute;

    // manual input in [service principal, managed idnetity, credential], has params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServiceEndpoint,
        association: 'SERVICE_ENDPOINT',
        templatable: true,
    })
    public serviceEndpoint: string;

    // input in service principal, has params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_Tenant,
        association: 'TENANT',
        templatable: true,
    })
    public tenant: string;

    // dropdown in service principal, no params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_AzureCloudType,
    })
    public azureCloudType?: azureCloudTypeDef;

    // input in service principal, has params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalID,
        association: 'SERVICE_PRINCIPAL_ID',
        templatable: true,
    })
    public servicePrincipalId: string;

    // readonly input/dropdown in service principal, no params
    @Metamodel.attribute({
        display: shellStringResources.ModelName_StorageAccountKind,
    })
    public accountKind: StorageAccountKind;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalKey,
        association: 'SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String'],
        constraint: {
            validator: (
                linkedservice: AzureBlobStorageLinkedService,
                referencedResource: AzureKeyVault,
                violation?: IViolation,
                context?: IValidationContext
            ): Promise<IViolation[] | undefined> => {
                if (
                    azureBlobStorageLinkedServiceValidators.validateAKVLinkedserviceReference
                ) {
                    return azureBlobStorageLinkedServiceValidators.validateAKVLinkedserviceReference(
                        linkedservice,
                        referencedResource, violation, context
                    );
                }
                return undefined;
            },
        },
    })
    public servicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalCredentialType,
    })
    public servicePrincipalCredentialType?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalCert,
        constraint: {
            validator: (
                linkedservice: AzureBlobStorageLinkedService,
                referencedResource: AzureKeyVault,
                violation?: IViolation,
                context?: IValidationContext
            ): Promise<IViolation[] | undefined> => {
                if (
                    azureBlobStorageLinkedServiceValidators.validateAKVLinkedserviceReference
                ) {
                    return azureBlobStorageLinkedServiceValidators.validateAKVLinkedserviceReference(
                        linkedservice,
                        referencedResource, violation, context
                    );
                }
                return undefined;
            },
        },
    })
    public servicePrincipalCert?: AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: (
                linkedservice: AzureBlobStorageLinkedService,
                referencedCredential: Credential
            ): IViolation[] | undefined => {
                if (
                    linkedservice &&
                    linkedservice.authenticationMethod ===
                    azureStorageAuthTypes.Credential &&
                    !referencedCredential
                ) {
                    return [
                        {
                            message: () =>
                                shellStringResources.Error_InvalidCredentialReferenced,
                        },
                    ];
                }
                return undefined;
            },
        },
    })
    public credential?: Credential;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ContainerUriName,
        association: 'CONTAINER_URI',
        templatable: true,
    })
    public containerUri?: string;

    public authReferenceMethod?: string = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
    public isOldServicePrincipalResource: boolean = false;

    public getContainerNameInUri(): string {
        let matchedResult;
        if (
            this.authenticationMethod === azureStorageAuthTypes.Anonymous &&
            this.containerUri
        ) {
            matchedResult = new RegExp(Constants.blobContainerUriPattern).exec(
                this.containerUri
            );
            return matchedResult?.[1];
        } else if (
            this.authenticationMethod ===
            azureStorageAuthTypes.SasUriAuthentication &&
            this.sasUri
        ) {
            matchedResult = new RegExp(Constants.blobSasUriPattern).exec(
                this.sasUri
            );
            return matchedResult?.[1];
        }
        return undefined;
    }

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public reset(): void {
        this.accountName = undefined;
        this.accountKey = undefined;
        this.accountKind = undefined;
        this.sasUri = undefined;
        this.serviceEndpoint = undefined;
        this.servicePrincipalId = undefined;
        this.servicePrincipalKey = undefined;
        this.referedAccountAKV = undefined;
        this.sasToken = undefined;
        this.referedSasUriAKV = undefined;
        this.azureCloudType = undefined;
        this.credential = undefined;
        this.tenant = undefined;
    }

    protected _getPEResourceName(): string {
        switch (this.authenticationMethod) {
            case azureStorageAuthTypes.AccountKeyAuthentication:
                return this.accountName || '';
            case azureStorageAuthTypes.MSIAuthentication:
            case azureStorageAuthTypes.ServicePrincipalAuthentication:
            case azureStorageAuthTypes.Credential:
            case azureStorageAuthTypes.UserAssignedManagedIdentity:
                if (
                    this.serviceEndpoint &&
                    this.serviceEndpoint.toLowerCase().startsWith('https://')
                ) {
                    return this.serviceEndpoint.substr(8).split('.')[0];
                }
                return '';
            case azureStorageAuthTypes.SasUriAuthentication:
                if (
                    this.sasUri &&
                    this.sasUri.toLowerCase().startsWith('https://')
                ) {
                    return this.sasUri.substr(8).split('.')[0];
                }
                return '';
            default:
                return '';
        }
    }
}
